import { memo, useState, useCallback, useRef, type DragEvent } from 'react';
import type { DragDropPhrase, DragDropResultRule, ScreenPosition, CharacterPortrait } from '../types';
import { DialogueBox } from './dialogue-box';
import styles from './drag-drop-game.module.css';

const DEFAULT_SCREEN_POS: ScreenPosition = { top: 3, left: 5, width: 90, height: 88 };

type GamePhase = 'selecting' | 'evaluating' | 'result' | 'victory';

interface DragDropGameProps {
  phrases: DragDropPhrase[];
  requiredSelections: number;
  resultRules: DragDropResultRule[];
  victoryReward: string;
  victorySubReward?: string;
  starsTotal: number;
  screenPosition?: ScreenPosition;
  speakerName?: string;
  characterData?: CharacterPortrait[];
  /** When true, hide the "Menu" button on victory so the player continues (Suivant) or restarts */
  hasNextScene?: boolean;
  onComplete: (result: {
    stars: number;
    totalStars: number;
    action: 'restart' | 'menu' | 'next';
  }) => void;
}

export const DragDropGame = memo(({
  phrases,
  requiredSelections,
  resultRules,
  victoryReward,
  victorySubReward,
  starsTotal,
  screenPosition,
  speakerName = 'Laurent (RSSI)',
  characterData = [],
  hasNextScene,
  onComplete,
}: DragDropGameProps) => {
  const pos = screenPosition || DEFAULT_SCREEN_POS;
  const screenStyle = {
    top: `${pos.top}%`,
    left: `${pos.left}%`,
    width: `${pos.width}%`,
    height: `${pos.height}%`,
  };

  const [phase, setPhase] = useState<GamePhase>('selecting');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [correctCount, setCorrectCount] = useState(0);
  const [feedbackIndex, setFeedbackIndex] = useState(0);
  const [stars, setStars] = useState(0);
  const [currentRule, setCurrentRule] = useState<DragDropResultRule | null>(null);
  const [dragOverSlot, setDragOverSlot] = useState<number | null>(null);
  const draggedIdRef = useRef<number | null>(null);

  const handleDragStart = useCallback((e: DragEvent, phraseId: number) => {
    if (selectedIds.includes(phraseId)) return;
    draggedIdRef.current = phraseId;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(phraseId));
    const target = e.currentTarget as HTMLElement;
    requestAnimationFrame(() => target.classList.add(styles.phraseCardDragging));
  }, [selectedIds]);

  const handleDragEnd = useCallback((e: DragEvent) => {
    draggedIdRef.current = null;
    (e.currentTarget as HTMLElement).classList.remove(styles.phraseCardDragging);
  }, []);

  const handleSlotDragOver = useCallback((e: DragEvent, slotIndex: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverSlot(slotIndex);
  }, []);

  const handleSlotDragLeave = useCallback(() => {
    setDragOverSlot(null);
  }, []);

  const handleSlotDrop = useCallback((e: DragEvent, slotIndex: number) => {
    e.preventDefault();
    setDragOverSlot(null);
    const phraseId = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (isNaN(phraseId)) return;

    setSelectedIds(prev => {
      if (prev.includes(phraseId)) return prev;
      if (prev.length >= requiredSelections) return prev;
      const next = [...prev];
      if (next[slotIndex] !== undefined) return prev;
      while (next.length <= slotIndex) next.push(-1);
      next[slotIndex] = phraseId;
      return next.filter(id => id !== -1);
    });
  }, [requiredSelections]);

  const handlePhraseClick = useCallback((phraseId: number) => {
    setSelectedIds(prev => {
      if (prev.includes(phraseId)) {
        return prev.filter(id => id !== phraseId);
      }
      if (prev.length >= requiredSelections) return prev;
      return [...prev, phraseId];
    });
  }, [requiredSelections]);

  const handleRemoveFromSlot = useCallback((phraseId: number) => {
    setSelectedIds(prev => prev.filter(id => id !== phraseId));
  }, []);

  const handleSubmit = useCallback(() => {
    if (selectedIds.length !== requiredSelections) return;

    const correct = selectedIds.filter(id => {
      const phrase = phrases.find(p => p.id === id);
      return phrase?.isCorrect;
    }).length;

    setCorrectCount(correct);
    setFeedbackIndex(0);
    setPhase('evaluating');
  }, [selectedIds, requiredSelections, phrases]);

  const handleFeedbackClick = useCallback(() => {
    const selectedPhrases = selectedIds.map(id => phrases.find(p => p.id === id)!);
    if (feedbackIndex < selectedPhrases.length - 1) {
      setFeedbackIndex(prev => prev + 1);
    } else {
      const rule = resultRules.find(
        r => correctCount >= r.minCorrect && correctCount <= r.maxCorrect
      );
      setCurrentRule(rule || null);
      setPhase('result');
    }
  }, [feedbackIndex, selectedIds, phrases, correctCount, resultRules]);

  const handleResultClick = useCallback(() => {
    if (currentRule?.action === 'retry') {
      setPhase('selecting');
      setSelectedIds([]);
      setCorrectCount(0);
      setFeedbackIndex(0);
      setCurrentRule(null);
    } else {
      const bonusStar = correctCount === phrases.filter(p => p.isCorrect).length ? 1 : 0;
      const totalStars = correctCount + bonusStar;
      setStars(totalStars);
      setPhase('victory');
    }
  }, [currentRule, correctCount, phrases]);

  const handleVictoryAction = useCallback((action: 'restart' | 'menu' | 'next') => {
    if (action === 'restart') {
      setPhase('selecting');
      setSelectedIds([]);
      setCorrectCount(0);
      setFeedbackIndex(0);
      setCurrentRule(null);
      setStars(0);
      return;
    }
    onComplete({ stars, totalStars: starsTotal, action });
  }, [onComplete, stars, starsTotal]);

  const renderStars = (count: number, total: number) => {
    return Array.from({ length: total }, (_, i) => (
      <span key={i} style={{ display: 'inline-block', transition: 'transform 0.3s', transform: i < count ? 'scale(1.1)' : 'scale(1)' }}>
        {i < count ? '\u2B50' : '\u2606'}
      </span>
    ));
  };

  // Victory screen
  if (phase === 'victory') {
    return (
      <div className={styles.overlay}>
        <div className={styles.screenArea} style={screenStyle}>
          <div className={styles.screenContent} style={{ position: 'relative' }}>
            <div className={styles.victoryOverlay}>
              <div className={styles.victoryCard}>
                <div className={styles.missionEndText}>Fin de mission</div>
                <div className={styles.victoryTitle}>Mission Terminée !</div>
                <div className={styles.victoryStars}>
                  {renderStars(stars, starsTotal)}
                </div>
                <div className={styles.victoryStarsText}>
                  {stars} / {starsTotal} etoiles
                </div>
                <div className={styles.victoryReward}>
                  <div className={styles.rewardLabel}>Recompenses</div>
                  <div className={styles.rewardName}>{victoryReward}</div>
                  {victorySubReward && (
                    <div className={styles.rewardSub}>{victorySubReward}</div>
                  )}
                </div>
                <div className={styles.victoryButtons}>
                  <button
                    className={`${styles.victoryButton} ${styles.restartBtn}`}
                    onClick={() => handleVictoryAction('restart')}
                  >
                    Recommencer
                  </button>
                  {!hasNextScene && (
                    <button
                      className={`${styles.victoryButton} ${styles.menuBtn}`}
                      onClick={() => handleVictoryAction('menu')}
                    >
                      Menu
                    </button>
                  )}
                  <button
                    className={`${styles.victoryButton} ${styles.nextBtn}`}
                    onClick={() => handleVictoryAction('next')}
                  >
                    {hasNextScene ? 'Continuer' : 'Suivant'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Result phase: RSSI overall verdict in DialogueBox + action button
  if (phase === 'result' && currentRule) {
    return (
      <div className={styles.overlay}>
        <div className={styles.screenArea} style={screenStyle}>
          <div className={styles.screenContent} style={{ position: 'relative' }}>
            <div className={styles.resultOverlay}>
              <div className={styles.resultCard}>
                <div className={`${styles.resultScore} ${
                  currentRule.action === 'continue' ? styles.resultScoreGood
                  : correctCount === 2 ? styles.resultScoreMedium
                  : styles.resultScoreBad
                }`}>
                  {correctCount} / {requiredSelections} bonnes observations
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogueBox
          speaker={speakerName}
          text={currentRule.response}
          onClick={handleResultClick}
          showContinue={true}
          characterId="laurent"
          characterData={characterData}
        />
      </div>
    );
  }

  // Evaluating phase: show notebook with indicators + DialogueBox cycling through feedbacks
  if (phase === 'evaluating') {
    const selectedPhrases = selectedIds.map(id => phrases.find(p => p.id === id)!);
    const currentPhrase = selectedPhrases[feedbackIndex];
    const feedbackText = `"${currentPhrase.text}"\n\n— ${currentPhrase.feedback}`;

    return (
      <div className={styles.overlay}>
        <div className={styles.screenArea} style={screenStyle}>
          <div className={styles.screenContent}>
            <div className={styles.phraseGrid}>
              {phrases.map((phrase) => {
                const selIdx = selectedIds.indexOf(phrase.id);
                const isSelected = selIdx !== -1;
                const isRevealed = isSelected && selIdx <= feedbackIndex;
                return (
                  <div
                    key={phrase.id}
                    className={`${styles.phraseCard} ${isSelected ? styles.phraseCardSelected : ''} ${
                      isRevealed ? (phrase.isCorrect ? styles.phraseCardCorrect : styles.phraseCardWrong) : ''
                    }`}
                  >
                    {phrase.text}
                    {isRevealed && (
                      <span className={styles.phraseIndicator}>
                        {phrase.isCorrect ? '\u2705' : '\u274C'}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            <div className={styles.dropZonePanel}>
              {Array.from({ length: requiredSelections }, (_, slotIdx) => {
                const filledPhraseId = selectedIds[slotIdx];
                const filledPhrase = filledPhraseId !== undefined
                  ? phrases.find(p => p.id === filledPhraseId)
                  : null;
                const isRevealed = filledPhrase && slotIdx <= feedbackIndex;

                return (
                  <div
                    key={slotIdx}
                    className={`${styles.dropSlot} ${styles.dropSlotFilled} ${
                      isRevealed ? (filledPhrase!.isCorrect ? styles.dropSlotCorrect : styles.dropSlotWrong) : ''
                    }`}
                  >
                    {filledPhrase?.text}
                    {isRevealed && (
                      <span className={styles.slotIndicator}>
                        {filledPhrase!.isCorrect ? '\u2705' : '\u274C'}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <DialogueBox
          speaker={speakerName}
          text={feedbackText}
          onClick={handleFeedbackClick}
          showContinue={true}
          characterId="laurent"
          characterData={characterData}
        />
      </div>
    );
  }

  // Phase: selecting
  const emptySlots = Array.from({ length: requiredSelections }, (_, i) => i);

  return (
    <div className={styles.overlay}>
      <div className={styles.screenArea} style={screenStyle}>
        <div className={styles.screenContent}>
          <div className={styles.instructionBanner}>
            Parmi les 8 propositions, selectionnez 4 signes que nous sommes victimes d&apos;une cyberattaque en les deposant dans la zone de droite.
          </div>
          <div className={styles.gameArea}>
          <div className={styles.phraseGrid}>
            {phrases.map((phrase) => {
              const isSelected = selectedIds.includes(phrase.id);
              return (
                <div
                  key={phrase.id}
                  className={`${styles.phraseCard} ${isSelected ? styles.phraseCardSelected : ''}`}
                  draggable={!isSelected}
                  onDragStart={(e) => handleDragStart(e, phrase.id)}
                  onDragEnd={handleDragEnd}
                  onClick={() => handlePhraseClick(phrase.id)}
                >
                  {phrase.text}
                </div>
              );
            })}
          </div>

          <div className={styles.dropZonePanel}>
            {emptySlots.map((slotIdx) => {
              const filledPhraseId = selectedIds[slotIdx];
              const filledPhrase = filledPhraseId !== undefined
                ? phrases.find(p => p.id === filledPhraseId)
                : null;
              const isActive = dragOverSlot === slotIdx && !filledPhrase;

              return (
                <div
                  key={slotIdx}
                  className={`${styles.dropSlot} ${isActive ? styles.dropSlotActive : ''} ${filledPhrase ? styles.dropSlotFilled : ''}`}
                  onDragOver={(e) => handleSlotDragOver(e, slotIdx)}
                  onDragLeave={handleSlotDragLeave}
                  onDrop={(e) => handleSlotDrop(e, slotIdx)}
                  onClick={() => filledPhrase && handleRemoveFromSlot(filledPhrase.id)}
                >
                  {filledPhrase ? (
                    <>
                      {filledPhrase.text}
                      <span className={styles.removeHint}>&times;</span>
                    </>
                  ) : (
                    `Observation ${slotIdx + 1}`
                  )}
                </div>
              );
            })}

            <div className={styles.submitArea}>
              <div className={styles.selectionCount}>
                {selectedIds.length} / {requiredSelections} selectionnees
              </div>
              <button
                className={styles.submitButton}
                onClick={handleSubmit}
                disabled={selectedIds.length !== requiredSelections}
              >
                Je suis pret !
              </button>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
});

DragDropGame.displayName = 'DragDropGame';
